/**
 * User app preferences stored in public.user_preferences.metadata.
 * Auth user_metadata is a fallback for instrument/level until a preferences row exists,
 * and for membership when the preferences row has no metadata.membership yet.
 */

export type UserPreferenceMetadata = Record<string, unknown>;

export function parsePreferenceMetadata(raw: unknown): UserPreferenceMetadata {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as UserPreferenceMetadata;
  }
  return {};
}

/** True when the user has a row in user_preferences (metadata is authoritative). */
export function resolvePreferenceMetadata(
  prefsRow: { metadata: unknown } | null | undefined,
  authMetadata: UserPreferenceMetadata | undefined,
): UserPreferenceMetadata {
  if (prefsRow != null) {
    return parsePreferenceMetadata(prefsRow.metadata);
  }
  return parsePreferenceMetadata(authMetadata);
}

export function readRowMembership(
  row: { metadata: unknown } | null | undefined,
): string | undefined {
  if (row == null) return undefined;
  const m = parsePreferenceMetadata(row.metadata).membership;
  return typeof m === 'string' ? m : undefined;
}

/**
 * Reads membership from user_preferences first, then falls back to auth user_metadata.
 * Avoids calling get-membership when auth already has the value (e.g. after WP login).
 */
export function resolveMembershipSource(
  prefsRow: { metadata: unknown } | null | undefined,
  authMetadata: UserPreferenceMetadata | undefined,
): string | undefined {
  return readRowMembership(prefsRow) ?? (parsePreferenceMetadata(authMetadata).membership as string | undefined);
}

export const WP_SYNC_SURVEY_LAST_FAILURE_KEY = 'wp_sync_survey_last_failure';

export function readWpSurveySyncFailure(
  metadata: UserPreferenceMetadata | null | undefined,
): string | undefined {
  const v = metadata?.[WP_SYNC_SURVEY_LAST_FAILURE_KEY];
  return typeof v === 'string' ? v : undefined;
}
